import React, { useMemo, useState } from 'react';

import styled from 'styled-components';

import Select from 'components/lib/Select';

import CMASummaryRoomTypeFilterDropdown from './CMASummaryRoomTypeFilter/CMASummaryRoomTypeFilterDropdown';
import { Option, RoomTypeGroupOption } from './types';

type Props = {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  options: Option[];
  selectedValues: number[];
  groupingValue: number;
  roomTypeComparisonType: number;
  comparisonSqftRange: number;
  showClosestBeyondRange: boolean;
  roomTypesPriceDisplay: number;
  groupedOptions: RoomTypeGroupOption[];
  otherOptionsLabel?: string;
  dataTestId?: string;
  onApply: (payload: {
    selectedValues: number[];
    groupingValue: number;
    roomTypeFilterComparisonType: number;
    comparisonSqftRange: number;
    showClosestBeyondRange: boolean;
    roomTypesPriceDisplay: number;
  }) => void;
};

const StyledSelect = styled(Select)`
  &.ant-select {
    font-family: var(--font-regular);
    font-size: 12px;
    height: 28px;
    color: inherit;
    color: var(--deep-blue);
    background-color: var(--lighter-gray);
    border-radius: 4px;

    &.active {
      color: var(--dark-blue);
      background-color: var(--faded-blue);
    }

    &.ant-select-focused {
      color: var(--dark-blue);
      background-color: var(--faded-blue);
      border: 1px solid var(--button-blue);
    }

    .ant-select-arrow {
      color: inherit;
      width: 16px;
      height: 16px;
      top: 12px;
    }
    .ant-select-arrow .anticon {
      width: 8px;
      height: 4.9px;
      margin: auto;
    }
    .ant-select-selector {
      color: inherit;
      .ant-select-selection-placeholder {
        color: inherit;
        font-size: 12px;
      }
      .ant-select-selection-item {
        color: inherit;
        font-size: 12px;
      }
    }
  }
`;

const Label = styled.span`
  font-family: var(--font-bold);
`;

const LabelBadge = styled.span`
  font-family: var(--font-bold);
  font-size: 10px;
  background-color: var(--positive-green);
  border-radius: 4px;
  padding: 3px 2px;
  margin-left: 4px;
  color: white;
`;

const RoomTypeDropdown = ({
  className,
  disabled = false,
  placeholder,
  options,
  selectedValues,
  groupingValue,
  roomTypeComparisonType,
  comparisonSqftRange,
  showClosestBeyondRange,
  roomTypesPriceDisplay,
  groupedOptions,
  onApply,
  otherOptionsLabel,
  dataTestId,
}: Props) => {
  const [open, setOpen] = useState(false);

  const appliedSelectedItems = useMemo(
    () => options.filter((option) => selectedValues.includes(option.value)),
    [options, selectedValues]
  );

  const placeholderInfo = appliedSelectedItems.length > 0 ? `${placeholder}:` : `${placeholder} (${otherOptionsLabel})`;

  const labelRender = () => {
    let labelInfo = '';

    if (appliedSelectedItems.length > 0) {
      labelInfo = appliedSelectedItems[0].label;
    }

    if (otherOptionsLabel) {
      labelInfo = `${labelInfo} (${otherOptionsLabel})`;
    }

    return (
      <>
        <Label>{placeholderInfo} </Label>
        {labelInfo}
        {appliedSelectedItems.length > 1 && <LabelBadge>+{appliedSelectedItems.length - 1}</LabelBadge>}
      </>
    );
  };

  return (
    <StyledSelect
      open={open}
      className={className}
      variant={'borderless'}
      onDropdownVisibleChange={(visible) => setOpen(visible)}
      disabled={disabled}
      placeholder={placeholderInfo}
      value={selectedValues}
      labelRender={labelRender}
      dataTestId={dataTestId}
      dropdownStyle={{
        padding: 0,
        borderRadius: 0,
        border: '1px solid var(--line-gray)',
        minWidth: 421,
      }}
      dropdownRender={() => (
        <CMASummaryRoomTypeFilterDropdown
          open={open}
          onClose={() => setOpen(false)}
          onApply={onApply}
          options={options}
          groupedOptions={groupedOptions}
          selectedValues={selectedValues}
          groupingValue={groupingValue}
          roomTypeComparisonType={roomTypeComparisonType}
          comparisonSqftRange={comparisonSqftRange}
          showClosestBeyondRange={showClosestBeyondRange}
          roomTypesPriceDisplay={roomTypesPriceDisplay}
        />
      )}
    />
  );
};

export default RoomTypeDropdown;
